import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calculator
} from 'lucide-react';
import { mockCampaigns } from '../../data/mockData';
import ExpenseCard from './ExpenseCard';
import ExpenseForm from './ExpenseForm';
import ExpenseDetails from './ExpenseDetails';

const ExpenseManagement = () => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setCampaigns(mockCampaigns.filter(campaign => !campaign.isDeleted));
    } catch (error) {
      toast.error(t('expenses.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetails(true);
  };

  const handleAddExpense = () => {
    if (!selectedCampaign) {
      toast.warning(t('expenses.selectCampaignFirst'));
      return;
    }
    setEditingExpense(null);
    setShowForm(true);
  };

  const handleEditExpense = (expenseType) => {
    setEditingExpense(expenseType);
    setShowForm(true);
  };

  const handleDeleteExpense = (expenseType) => {
    if (window.confirm(t('expenses.deleteConfirmation'))) {
      try {
        // Update campaign expenses
        const updatedCampaigns = campaigns.map(campaign => {
          if (campaign.id === selectedCampaign.id) {
            const updatedExpenses = { ...campaign.expenses };
            delete updatedExpenses[expenseType];
            return { ...campaign, expenses: updatedExpenses };
          }
          return campaign;
        });
        setCampaigns(updatedCampaigns);
        setSelectedCampaign(prev => ({
          ...prev,
          expenses: { ...prev.expenses, [expenseType]: undefined }
        }));
        toast.success(t('expenses.deleteSuccess'));
      } catch (error) {
        toast.error(t('expenses.deleteError'));
      }
    }
  };

  const handleSaveExpense = (expenseData) => {
    try {
      const updatedCampaigns = campaigns.map(campaign => {
        if (campaign.id === selectedCampaign.id) {
          const updatedExpenses = {
            ...campaign.expenses,
            [expenseData.type]: expenseData.amount
          };
          return { ...campaign, expenses: updatedExpenses };
        }
        return campaign;
      });
      setCampaigns(updatedCampaigns);
      setSelectedCampaign(prev => ({
        ...prev,
        expenses: { ...prev.expenses, [expenseData.type]: expenseData.amount }
      }));
      setShowForm(false);
      toast.success(t('expenses.saveSuccess'));
    } catch (error) {
      toast.error(t('expenses.saveError'));
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.campaignName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || campaign.campaignType === filterType;
    return matchesSearch && matchesFilter;
  });

  const calculateTotalExpenses = (expenses) => {
    return Object.values(expenses || {}).reduce((total, amount) => total + (amount || 0), 0);
  };

  const getExpenseStats = () => {
    const totalCampaigns = filteredCampaigns.length;
    const totalExpenses = filteredCampaigns.reduce((sum, campaign) => 
      sum + calculateTotalExpenses(campaign.expenses), 0
    );
    const avgExpensePerCampaign = totalCampaigns > 0 ? totalExpenses / totalCampaigns : 0;
    
    return { totalCampaigns, totalExpenses, avgExpensePerCampaign };
  };

  const stats = getExpenseStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('expenses.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('expenses.subtitle')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('expenses.totalCampaigns')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalCampaigns}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <Calculator className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('expenses.totalExpenses')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${stats.totalExpenses.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900 rounded-full">
              <TrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('expenses.avgPerCampaign')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${stats.avgExpensePerCampaign.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-full">
              <TrendingUp className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder={t('expenses.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex gap-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('expenses.allTypes')}</option>
              <option value="internal">{t('campaigns.internal')}</option>
              <option value="sold">{t('campaigns.sold')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Campaign List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('expenses.campaignList')}
          </h2>
        </div>
        <div className="p-6">
          {filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <Calculator className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {t('expenses.noCampaignsFound')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCampaigns.map((campaign) => (
                <ExpenseCard
                  key={campaign.id}
                  campaign={campaign}
                  onSelect={() => handleCampaignSelect(campaign)}
                  totalExpenses={calculateTotalExpenses(campaign.expenses)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Expense Details Modal */}
      {showDetails && selectedCampaign && (
        <ExpenseDetails
          campaign={selectedCampaign}
          onClose={() => {
            setShowDetails(false);
            setSelectedCampaign(null);
          }}
          onAddExpense={handleAddExpense}
          onEditExpense={handleEditExpense}
          onDeleteExpense={handleDeleteExpense}
        />
      )}

      {/* Expense Form Modal */}
      {showForm && (
        <ExpenseForm
          campaign={selectedCampaign}
          editingExpense={editingExpense}
          onClose={() => setShowForm(false)}
          onSave={handleSaveExpense}
        />
      )}
    </div>
  );
};

export default ExpenseManagement;